import { firebaseAuth } from './firebase.service';
import { User, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { isProd } from '../environment';
import axios, { AxiosError } from 'axios';
import { JsonObject } from '../helpers';
import { getUnixTime } from 'date-fns';
import { getUrlQueryParam, removeUrlQueryParam } from '../url-helpers';
import { ApiResponse, handleResponse } from '.';
import { post } from './sp-api.service';
import { FirebaseError } from 'firebase/app';
import { debounce } from 'lodash';

export type AuthRole = 'org:owner' | 'org:admin' | 'org:auditor';

export const AuthRoleLabels: Record<AuthRole, string> = {
  'org:owner': 'Owner',
  'org:admin': 'Admin',
  'org:auditor': 'Auditor',
};

export type AuthPermission = 'manage-admins' | 'configure-conversation-export' | 'read' | 'write';

export type AuthUser = User & {
  token: string;
  orgId: string;
  role: AuthRole | null;
  assumed: boolean;
  spUserId?: string;
  intercomUser?: string;
};

class AuthProviderModel {
  user: AuthUser | null = null;

  get token(): string {
    return this.user?.token || '';
  }

  get orgId(): string {
    return this.user?.orgId || '';
  }
}

export const AuthProvider = new AuthProviderModel();

type TokenResponse = {
  jwt: string;
};

const axiosClient = axios.create({
  baseURL: '',
  timeout: 10000,
  withCredentials: false,
  responseType: 'json',
  headers: {
    'X-Requested-By': 'surepath',
  },
});

axiosClient.interceptors.request.use((config) => {
  if (AuthProvider.token) {
    config.headers['Surepath-Authorization'] = `Bearer ${AuthProvider.token}`;
  }

  return config;
});

const get = async (path: string, params?: JsonObject): Promise<ApiResponse> => {
  return axiosClient.get(path, { params }).then(handleResponse);
};

export const userCan = (user: AuthUser | null | undefined, perm: AuthPermission): boolean => {
  const role = user?.role;
  if (!role) {
    return false;
  }

  if (role === 'org:owner') {
    return true;
  }

  switch (perm) {
    case 'read':
      return ['org:admin', 'org:auditor'].includes(role);
    case 'configure-conversation-export':
    case 'manage-admins':
      return false;
    case 'write':
      return ['org:admin'].includes(role);
  }
};

const getBaseUrl = (subdomain: 'auth' | 'admin' | 'partner' | 'portal'): string => {
  let domain = `${subdomain}.surepath.ai`;

  if (!isProd()) {
    const parts = window.location.hostname.split('.');
    // Preview domains:  pr-{N}.admin.{env}.surepath.ai  (5 parts)
    // Standard domains: admin.{env}.surepath.ai          (4 parts)
    const isPreview = parts[0].startsWith('pr-') && parts.length === 5;
    const environment = isPreview ? parts[2] : parts[1];

    if (['dev', 'stage', 'local'].includes(environment)) {
      domain = `${subdomain}.${environment}.surepath.ai`;
    }
  }

  return `https://${domain}`;
};

const getAuthBaseUrl = (): string => getBaseUrl('auth');

const getPartnerBaseUrl = (): string => getBaseUrl('partner');

export const getPortalBaseUrl = (): string => getBaseUrl('portal');

export const getAuthCallbackUrl = (): string => `${getAuthBaseUrl()}/__/auth/handler`;

export const getSamlEntityId = (tenantId: string): string => `${getAuthBaseUrl()}/${tenantId}`;

export const getAuthNoAccessUrl = (): string => {
  return `${getAuthBaseUrl()}/splash?action=admin-access`;
};

export const getAuthRedirectUrl = (fromAuthSite?: boolean): string => {
  // user was just redirected here from auth? don't redirect them back
  const fas = typeof fromAuthSite === 'boolean' ? fromAuthSite : getFromAuthSite();
  if (fas) {
    return '/error?code=500';
  }

  const redirectDest = encodeURIComponent(window.location.origin);
  return `${getAuthBaseUrl()}/login?redirect_to=${redirectDest}`;
};

export const getFromAuthSite = (): boolean => {
  const fromAuthSite = getUrlQueryParam('fas');
  removeUrlQueryParam('fas');
  return !!fromAuthSite;
};

export const getAssumeFlag = (): boolean => {
  const assumeFlag = getUrlQueryParam('assume');
  removeUrlQueryParam('assume');
  return !!assumeFlag;
};

export const signOut = async (redirect = true): Promise<boolean> => {
  try {
    await firebaseAuth.signOut();

    if (redirect) {
      window.location.href = `${getAuthBaseUrl()}/logout?redirect_to=${window.location.origin}`;
    }
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const getCurrentUser = async (skipFirebase = false): Promise<AuthUser | null> => {
  const fbAuthUser = skipFirebase ? null : await getCurrentFirebaseUser();

  if (fbAuthUser) {
    console.log('auth from fb-ls');
    AuthProvider.user = fbAuthUser;
    return fbAuthUser;
  }

  const spAuthUser = await getCurrentTokenUser();

  if (spAuthUser) {
    console.log('auth from sp-token');
    AuthProvider.user = spAuthUser;
    return spAuthUser;
  }

  return null;
};

/*
 * Attempts to keep the JWT fresh, and keep AuthProvider in sync with the latest from Firebase
 */
export const syncAndRefreshTokens = () => {
  /*
   * Keep AuthProvider in sync with the latest from Firebase
   *
   * Note that this handler will only fire when methods of firebaseAuth are being called, and does not fire when
   * authentication expires:
   *
   * https://github.com/firebase/firebase-js-sdk/issues/2985
   * https://github.com/firebase/firebase-js-sdk/blob/e9ff107eedbb9ec695ddc35e45bdd62734735674/packages/auth/src/core/index.ts#L136
   */
  firebaseAuth.onIdTokenChanged((user: User) => {
    console.log('local auth change', user?.uid);

    if (!user) {
      AuthProvider.user = null;
      safeAuthRedirect();
      return;
    }

    getAuthUser(user).then((authUser) => {
      /*
       * This condition checks for an edge case where a partner or root user may somehow attempt to
       * assume a role into multiple orgs in the same browser, at the same time. In that case, the
       * second assume attempt would overwrite firebase local credentials causing both admin tabs
       * to "switch" to the second assumed org.
       */
      if (AuthProvider.orgId && authUser?.orgId && AuthProvider.orgId !== authUser.orgId) {
        console.warn('detected org change from auth observer, reloading...');
        window.location.reload();
        return;
      }

      AuthProvider.user = authUser;
    });
  });

  const intMinutes = 1000 * 60 * 8;

  /*
   * Periodically refresh the Firebase Id Token
   *
   * Ensures that other, non-firebase services can always have an up-to-date token. Note that while
   * Firebase will lazy-refresh the token when calling getIdToken/getIdTokenResult, we need a lightweight,
   * synchronous way of referencing an up-to-date token.
   */
  setInterval(() => {
    firebaseAuth.currentUser
      ?.getIdTokenResult()
      .then((result) => {
        const expTime = result?.claims?.exp;
        if (!expTime) {
          return;
        }

        const minuteDiff = (Number(expTime) - getUnixTime(new Date())) / 60;

        if (minuteDiff < 20) {
          refreshTokenUser();
        }
      })
      .catch(handleTokenRefreshError);
  }, intMinutes);
};

/*
 * Attempt to force-refresh the firebase id token. Handle cases of invalid credentials, or missing current user by
 * redirecting back to auth-ui
 */
const refreshTokenUser = () => {
  if (!firebaseAuth.currentUser) {
    safeAuthRedirect();
    return;
  }

  firebaseAuth.currentUser.getIdToken(true).catch(handleTokenRefreshError);
};

/*
 * This function is used by the TanStack Query QueryClient to attempt to refresh credentials, and redirect to
 * the auth site, when a query fails and returns a 401. It is debounced to prevent multiple concurrent or consecutive
 * fetch calls from triggering overlapping refresh requests.
 */
export const safeRefreshTokenUser = debounce(refreshTokenUser, 2000, {
  leading: true,
  trailing: false,
});

const handleTokenRefreshError = (err: unknown) => {
  const { code } = err as FirebaseError;
  if (
    ['auth/id-token-revoked', 'auth/user-token-expired', 'auth/id-token-expired'].includes(code)
  ) {
    safeAuthRedirect();
  }

  // @todo any other codes we want to handle?
  // https://firebase.google.com/docs/auth/admin/errors
};

/*
 * If a user has multiple admin-ui tabs open, then signs out from one of the tabs, this redirect will be
 * triggered by the firebaseAuth.onIdTokenChanged handler defined above. In that case, we need to give
 * the "sign out" tab a chance to sign out of admin-ui, then redirect to auth-ui and sign out. Otherwise,
 * this tab will immediately redirect to auth-ui, use still-valid credentials there to pull a fresh
 * sp-token, and then redirect back to admin-ui and use the sp-token to sign in.
 */
const safeAuthRedirect = debounce(
  () => {
    window.location.href = getAuthRedirectUrl();
  },
  4000,
  { leading: false, trailing: true }
);

const getCurrentTokenUser = async (): Promise<AuthUser | null> => {
  let spAccessToken = '',
    spTenantId = '';

  try {
    const response = await get('/auth/status');

    const { accessToken, tenantId, assumedCustomToken } =
      (response as { accessToken: string; tenantId: string; assumedCustomToken: string }) || {};

    if (!tenantId) {
      return null;
    }

    spTenantId = tenantId;

    // role assumption response
    if (assumedCustomToken) {
      spAccessToken = assumedCustomToken;
      // normal login
    } else if (accessToken) {
      spAccessToken = accessToken;
    }
  } catch (err) {
    const axiosError = err as AxiosError;
    // cookie is probably just missing
    if (axiosError.response?.status === 400) {
      return null;
    }
    console.error(err);
    return null;
  }

  if (!spAccessToken || !spTenantId) {
    return null;
  }

  firebaseAuth.tenantId = spTenantId;

  const result = await signInWithCustomToken(firebaseAuth, spAccessToken);

  if (!result?.user) {
    return null;
  }

  return getAuthUser(result.user);
};

const getCurrentFirebaseUser = async (forceRefresh?: boolean): Promise<AuthUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (!user) {
        resolve(null);
        return;
      }

      unsubscribe();

      getAuthUser(user, forceRefresh).then(resolve);
    });
  });
};

const isValidCalimValue = (claimVal: string | null | undefined): boolean =>
  !!claimVal && claimVal !== 'none';

/*
 * Given a Firebase User, get their parsed JWT and then validate that important
 * authentication parameters are present. Return an AuthUser to act as the primary
 * interface for the auth service and the rest of the application, as opposed to directly
 * handling the Firebase User object.
 */
const getAuthUser = async (user: User, forceRefresh = false): Promise<AuthUser | null> => {
  const userWithToken = user as User & { accessToken: string };

  // user missing critical info
  if (!userWithToken.tenantId || !userWithToken.accessToken) {
    console.error('user missing tenant id or access token');
    return null;
  }

  const result = await user.getIdTokenResult(forceRefresh).catch((err) => {
    console.error(err);
  });
  const { claims, token } = result || {};

  if (!claims || !token) {
    console.error('user missing claims or token');
    return null;
  }

  const { org_id, role, assumed, sp_user_id, intercom_user } = claims as {
    org_id: string;
    role: string;
    assumed: boolean;
    sp_user_id: string;
    intercom_user: string;
  };

  /*
   * Rather than returning null here, simply warn and then return an AuthUser
   * object which does not have read permissions. This way, the user can be
   * redirected to the admin-no-access page, rather than being handled as if
   * they are not logged in.
   */
  if (!isValidCalimValue(org_id) || !isValidCalimValue(role)) {
    console.warn('user missing org_id/role', claims);
  }

  return {
    ...user,
    orgId: org_id || '',
    role: (role as AuthRole) || null,
    token,
    assumed: !!assumed,
    spUserId: sp_user_id || undefined,
    intercomUser: intercom_user || undefined,
  };
};

export const deactivateAssumeRole = async (): Promise<boolean> => {
  const response = await post('/auth/assume/return', {});

  if (response?.status !== 'ok') {
    return false;
  }

  // @todo should we logout of firebase here? hitting the return endpoint nukes refresh tokens...

  window.location.href = getPartnerBaseUrl();

  return true;
};

const getToken = async (path: string): Promise<TokenResponse> => {
  const response = (await get(path).catch((err) => {
    console.error('Token fetch failed:', err);
    return { jwt: '' };
  })) as TokenResponse;
  return response;
};

export const getAnalyticsToken = async (): Promise<TokenResponse> =>
  getToken('/auth/analytics/token');

export const getSupportToken = async (): Promise<TokenResponse> => getToken('/auth/support/token');
